import React from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';

interface Props {
  backRoute: string;
}

/**
 * Skeleton for CertificationSnapshotSectionDetailPage while answers are loading.
 * Each shimmer row mirrors the QITAnswerRow layout: a short label line, a wider
 * value line, and an optional narrow date line.
 */
export function CertificationSnapshotSectionDetailSkeleton({
  backRoute,
}: Props): React.ReactElement {
  const rows = [
    { labelWidth: '45%', valueWidth: '70%', showDate: true },
    { labelWidth: '55%', valueWidth: '40%', showDate: false },
    { labelWidth: '35%', valueWidth: '60%', showDate: true },
    { labelWidth: '50%', valueWidth: '55%', showDate: false },
    { labelWidth: '40%', valueWidth: '75%', showDate: true },
    { labelWidth: '60%', valueWidth: '45%', showDate: false },
    { labelWidth: '38%', valueWidth: '65%', showDate: true },
  ];

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PageHeader title="" backRoute={backRoute} />

      <Box
        aria-busy="true"
        aria-label="Loading section answers"
        sx={{
          flex: 1,
          bgcolor: 'background.paper',
          mx: 2,
          mb: 2,
          borderRadius: '16px',
          boxShadow: '0 1px 8px rgba(0,0,0,0.03)',
          overflow: 'hidden',
        }}
      >
        {rows.map((row, i) => (
          <Stack
            key={i}
            direction="row"
            alignItems="center"
            sx={{
              py: 1.5,
              px: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Question label line */}
              <Skeleton
                variant="text"
                width={row.labelWidth}
                height={14}
                animation="wave"
                sx={{ mb: 0.5 }}
              />
              {/* Answer value line */}
              <Skeleton
                variant="text"
                width={row.valueWidth}
                height={20}
                animation="wave"
              />
              {/* Date line */}
              {row.showDate && (
                <Skeleton
                  variant="text"
                  width="30%"
                  height={12}
                  animation="wave"
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
          </Stack>
        ))}
      </Box>
    </Box>
  );
}
